/**
 * One ECharts wrapper, replacing both `company/charts/EChart.tsx` and
 * `queues/charts/EChart.tsx` (spec §4.4). Registers only the modules the 19
 * existing charts actually use: `BarChart`, `LineChart`, `PieChart`,
 * `HeatmapChart` + `GridComponent`, `TooltipComponent`, `LegendComponent`,
 * `TitleComponent`, `VisualMapComponent` + `CanvasRenderer`. Both old
 * wrappers also registered `DataZoomComponent`, `MarkLineComponent`, and
 * `MarkAreaComponent`, but the chart-feature-inventory audit found zero
 * charts using any of the three -- they are deliberately NOT registered
 * here. If a later migration needs one, register it there explicitly rather
 * than assuming it is already available.
 */
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, HeatmapChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { ECElementEvent, EChartsOption } from 'echarts';
import { useAuth } from '../../auth/TeamsAuthProvider';
import { ensureChartTheme } from './chartTheme';

let modulesRegistered = false;
function registerModules() {
  if (modulesRegistered) {
    return;
  }
  echarts.use([
    BarChart,
    LineChart,
    PieChart,
    HeatmapChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    VisualMapComponent,
    CanvasRenderer,
  ]);
  modulesRegistered = true;
}

export interface ChartProps {
  option: EChartsOption;
  height?: number | string;
  loading?: boolean;
  onEvent?: Record<string, (params: ECElementEvent) => void>;
  /** Required: the chart has no visible built-in legend/label a screen reader can use instead. */
  ariaLabel: string;
}

export function Chart({ option, height = 280, loading, onEvent, ariaLabel }: ChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);
  const { themeName } = useAuth();

  // Init (and re-init on theme change -- ECharts binds its theme at
  // `init()` time, so there is no way to swap it on a live instance).
  // Uses `ResizeObserver` on the container instead of `window.resize`: the
  // exact gap both old wrappers had per the inventory's cross-cutting notes
  // (a container that resizes without a window resize event, e.g. inside a
  // flex/grid layout change, never repainted).
  useEffect(() => {
    registerModules();
    if (!containerRef.current) {
      return;
    }
    ensureChartTheme(themeName);
    const instance = echarts.init(containerRef.current, themeName);
    instanceRef.current = instance;

    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      instance.dispose();
      instanceRef.current = null;
    };
  }, [themeName]);

  // Re-applied on `themeName` change too, since the instance above was just
  // recreated and would otherwise render blank until `option` next changes.
  useEffect(() => {
    instanceRef.current?.setOption(option, { notMerge: true });
  }, [option, themeName]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance) {
      return;
    }
    if (loading) {
      instance.showLoading();
    } else {
      instance.hideLoading();
    }
  }, [loading, themeName]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !onEvent) {
      return;
    }
    const handlers = Object.entries(onEvent);
    // echarts' `on(event: string, ...)` overload (used here because the
    // event name isn't a literal) types the handler generically as
    // `(...args: unknown[]) => void | boolean` -- the element-click events
    // this component actually wires up all deliver an `ECElementEvent`, so
    // this cast reflects the real shape without widening it to `any`.
    handlers.forEach(([evt, fn]) =>
      instance.on(evt, fn as (...args: unknown[]) => void | boolean),
    );
    return () => {
      handlers.forEach(([evt, fn]) => instance.off(evt, fn));
    };
  }, [onEvent, themeName]);

  return (
    // `role="img"` fits every chart today (none is interactive for
    // assistive tech). Once Task 2 wires real `onEvent` click-to-filter
    // charts (KpiDonut/CountryBreakdown/RegionBreakdown), reconsider this --
    // a clickable chart may need a different role/label story than a
    // static image does.
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      style={{ width: '100%', height }}
    />
  );
}
